/** Clamp a number into [min, max]; NaN falls back to min. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const match = HEX.exec(hex.trim());
  if (!match) return { r: 0, g: 0, b: 0 };
  let body = match[1]!;
  if (body.length === 3) {
    body = body
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

/** `#a259c6` + 40 → `rgba(162, 89, 198, 0.4)`. Opacity is a 0–100 percentage. */
export function withAlpha(hex: string, opacityPercent: number): string {
  const { r, g, b } = hexToRgb(hex);
  const a = clamp(opacityPercent, 0, 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function linearize(value: number): number {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance per WCAG 2.1, used to pick readable text on a colour swatch. */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

export function readableTextColor(hex: string): string {
  return luminance(hex) > 0.45 ? '#000000' : '#ffffff';
}

export function normalizeHex(value: string, fallback = '#000000'): string {
  const match = HEX.exec(value.trim());
  if (!match) return fallback;
  let body = match[1]!;
  if (body.length === 3) {
    body = body
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${body.toLowerCase()}`;
}
